import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, withCtx as _withCtx, createCommentVNode as _createCommentVNode, unref as _unref, createElementVNode as _createElementVNode, createVNode as _createVNode, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-f8cadd70"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "records-holder" }
const _hoisted_2 = { key: 5 }

import { ref, computed, inject, PropType, watch, onMounted } from "vue";
import popoverBase from "@/components/ui/popovers/popover-base.vue";
import { NotificationType } from "@/enums/notifications/NotificationType";
import { SignalRNotification } from "@/types/notifications/SignalRNotification";
import EditTaskModal from "@/components/tasks/edit-task-modal.vue";
import EditAppointmentModal from "@/components/ui/diary/edit-appointment-modal.vue";
import { useNotificationsStore } from "@/stores/NotificationsStore";
import { AssignedUserAcknowledged } from "@/enums/tasks/AssignedUserAcknowledged";
import displayErrorMessage from "@/utils/ErrorHandling";
import { getErrorMessage } from "bright-logic-core/src/errors";
import { ContactTasks } from "acquaint-api/models/ContactTasks";
import { AcquaintApiClient } from "@/services/AcquaintApiClient";
import { useRouter } from "vue-router";
import { RouteNames } from "@/router/RouteNames";
import NotificationCard from "@/components/notifications/notification-card.vue";
import NotificationsContainer from "@/components/notifications/notifications-container.vue";
import ReturnedSignableDocumentsModal from "@/components/signable-documents/returned-signable-documents-modal.vue";
import { ReminderFilters } from "@/types/reminders/ReminderFilters";
import { useCurrentUserStore } from "@/stores/CurrentUserStore";
import { CurrentUser } from "@/types/user/CurrentUser";
import { useLookupConfigurationStore } from "@/stores/LookupConfigurationStore";
import { LookupTableSource } from "acquaint-api/constants/LookupTableSource";
import MaintenanceRequestsModal from "@/components/maintenance-requests/maintenance-requests-modal.vue";


export default /*#__PURE__*/_defineComponent({
  __name: 'notifications-popup',
  props: {
  /**
   * The boolean value used with `v-model` to control the visibility or state of a modal or component.
   * @type {boolean}
   * @required
   */
  modelValue: {
    type: Boolean,
    required: true,
  },
  /**
   * Indicates the target.
   * @type {string}
   * @required
   */
  target: {
    type: String,
    required: true,
  },
  /**
   * Latest Notifications.
   * @type Array as PropType<SignalRNotification[]>
   * @default: () => ([])
   */
  latestNotifications: {
    type: Array as PropType<SignalRNotification[]>,
    default: () => [],
  },
},
  emits: [
  /**
   * Triggered when the `modelValue` changes.
   * This is used to update the v-model variable in the parent component and control the visibility of the modal.
   * @event update:modelValue
   * @property {boolean} value - The updated state to reflect whether the modal should be shown or hidden.
   */
  "update:modelValue",
],
  setup(__props, { emit }) {

const props = __props;





const showPopOver = computed({
  get: () => props.modelValue,
  set: (value: Boolean) => emit("update:modelValue", value),
});

const notificationsStore = useNotificationsStore();
const tasksNotifications = ref<SignalRNotification[]>([]);
const appointmentsNotifications = ref<SignalRNotification[]>([]);
const returnedSignableNotifications = ref<SignalRNotification[]>([]);
const maintenanceRequestNotifications = ref<SignalRNotification[]>([]);
const displayEditTaskModal = ref(false);
const displayEditAppointmentModal = ref(false);
const displayReturnedSignableDocumentsModal = ref(false);
const displayMaintenanceRequestsModal = ref(false);
const selectedNotificationId = ref(0);
const taskDetails = ref<ContactTasks>();
const loading = ref(false);
const apiClient = inject("AcquaintApiClient") as AcquaintApiClient;
const systemNotifications = ref<SignalRNotification[]>([]);
const router = useRouter();
const lookupStore = useLookupConfigurationStore();
const currentUserStore = useCurrentUserStore();
const currentUser = ref<CurrentUser>();
const filters = ref<ReminderFilters>({
  user: { id: -1, description: "All" },
  branch: { id: -1, description: "All" },
});

onMounted(async () => {
  currentUser.value = await currentUserStore.getCurrentUserDetails();
  await setDefaultFilters();
});

const setDefaultFilters = async () => {
  const userId = currentUser.value?.id ?? 0;
  const siteId = currentUser.value?.siteID ?? -1;

  filters.value.user = {
    id: userId,
    description: await lookupStore.getLookupConfigurationDescription(
      LookupTableSource.Users,
      userId,
    ),
  };

  filters.value.branch = {
    id: siteId,
    description: await lookupStore.getLookupConfigurationDescription(
      LookupTableSource.Sites,
      siteId,
    ),
  };
};

const renderNotifications = (allNotifications: SignalRNotification[]) => {
  tasksNotifications.value = [];
  appointmentsNotifications.value = [];
  systemNotifications.value = [];
  returnedSignableNotifications.value = [];
  maintenanceRequestNotifications.value = [];

  allNotifications.forEach((notification: SignalRNotification) => {
    switch (notification.type) {
      case NotificationType.Task:
        tasksNotifications.value.push(notification);
        break;
      case NotificationType.Appointment:
        appointmentsNotifications.value.push(notification);
        break;
      case NotificationType.RemindersRefreshed:
        systemNotifications.value.push(notification);
        break;
      case NotificationType.SignableDocument:
        returnedSignableNotifications.value.push(notification);
        break;
      case NotificationType.MaintenanceRequest:
        maintenanceRequestNotifications.value.push(notification);
        break;
      default:
        systemNotifications.value.push(notification);
        break;
    }
  });
};

const popupDisplayed = async () => {
  tasksNotifications.value.length = 0;
  appointmentsNotifications.value.length = 0;
  returnedSignableNotifications.value.length = 0;
  maintenanceRequestNotifications.value.length = 0;

  const allNotifications: SignalRNotification[] =
    notificationsStore.getNotifications();
  if (allNotifications.length > 0) {
    renderNotifications(allNotifications);
  }
};

// If the modal is opened and the lastest notifications fetched from the toolbar are different to the ones already displayed here, the displayed lists are updated.
watch(
  () => props.latestNotifications,
  () => {
    if (
      JSON.stringify([
        ...tasksNotifications.value,
        ...appointmentsNotifications.value,
        ...returnedSignableNotifications.value,
        ...maintenanceRequestNotifications.value,
      ]) != JSON.stringify(props.latestNotifications) &&
      showPopOver.value
    ) {
      renderNotifications(props.latestNotifications);
    }
  },
  { immediate: true },
);

const loadNotificationRecord = (notification: SignalRNotification) => {
  selectedNotificationId.value = notification.linkId;
  notificationsStore.removeNotification(notification);
  showPopOver.value = false;

  switch (notification.type) {
    case NotificationType.Appointment:
      appointmentsNotifications.value = appointmentsNotifications.value.filter(
        (item) => {
          return item.linkId !== notification.linkId;
        },
      );
      displayEditAppointmentModal.value = true;
      break;
    case NotificationType.Task:
      tasksNotifications.value = tasksNotifications.value.filter((item) => {
        return item.linkId !== notification.linkId;
      });
      displayEditTaskModal.value = true;
      break;
    case NotificationType.SignableDocument:
      returnedSignableNotifications.value =
        returnedSignableNotifications.value.filter((item) => {
          return item.linkId !== notification.linkId;
        });
      displayReturnedSignableDocumentsModal.value = true;
      break;
    case NotificationType.MaintenanceRequest:
      maintenanceRequestNotifications.value =
        maintenanceRequestNotifications.value.filter((item) => {
          return item.linkId !== notification.linkId;
        });
      displayMaintenanceRequestsModal.value = true;
      break;
    case NotificationType.RemindersRefreshed:
      router.push({ name: RouteNames.Reminders });
      break;
  }
};

const clearTasks = () => {
  tasksNotifications.value.forEach((task) => {
    notificationsStore.removeNotification(task);
  });
  tasksNotifications.value = [];
};

const clearAppointments = () => {
  appointmentsNotifications.value.forEach((appointment) => {
    notificationsStore.removeNotification(appointment);
  });
  appointmentsNotifications.value = [];
};
const clearReturnedSignableDocuments = () => {
  returnedSignableNotifications.value.forEach((appointment) => {
    notificationsStore.removeNotification(appointment);
  });
  returnedSignableNotifications.value = [];
};

const clearMaintenanceRequests = () => {
  maintenanceRequestNotifications.value.forEach((request) => {
    notificationsStore.removeNotification(request);
  });
  maintenanceRequestNotifications.value = [];
};

const removeTask = async (notification: SignalRNotification, index: number) => {
  await fetchTaskData(notification.linkId);

  if (taskDetails.value) {
    taskDetails.value.assignedUserAcknowledged =
      AssignedUserAcknowledged.Acknowledged;
    await updateTask(notification.linkId);
  }
  notificationsStore.removeNotification(notification);
  tasksNotifications.value.splice(index, 1);
};

const updateTask = async (id: number) => {
  try {
    await apiClient.httpPut(`database/contacttasks/${id}`, taskDetails.value);
  } catch (err) {
    displayErrorMessage(getErrorMessage(err));
  }
};

const fetchTaskData = async (id: number) => {
  try {
    taskDetails.value = await apiClient.httpGet(`database/contacttasks/${id}`);
    return true;
  } catch (err) {
    displayErrorMessage(getErrorMessage(err));
    return false;
  }
};

const removeAppointment = (
  notification: SignalRNotification,
  index: number,
) => {
  notificationsStore.removeNotification(notification);
  appointmentsNotifications.value.splice(index, 1);
};

const removeReturnedSignableDocument = (
  notification: SignalRNotification,
  index: number,
) => {
  notificationsStore.removeNotification(notification);
  returnedSignableNotifications.value.splice(index, 1);
};

const removeMaintenanceRequest = (
  notification: SignalRNotification,
  index: number,
) => {
  notificationsStore.removeNotification(notification);
  maintenanceRequestNotifications.value.splice(index, 1);
};

const removeSystemNotification = (
  notification: SignalRNotification,
  index: number,
) => {
  notificationsStore.removeNotification(notification);
  systemNotifications.value.splice(index, 1);
};

const clearSystemNotifications = () => {
  systemNotifications.value.forEach((notification) =>
    notificationsStore.removeNotification(notification),
  );
  systemNotifications.value = [];
};

const height = computed(() => window.outerHeight);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(popoverBase, {
      modelValue: showPopOver.value,
      "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((showPopOver).value = $event)),
      target: props.target,
      width: "370px",
      height: `${height.value}px`,
      onPopupDisplayed: popupDisplayed,
      loading: loading.value,
      title: "Notifications",
      "show-close-button": true
    }, {
      content: _withCtx(() => [
        _createElementVNode("section", _hoisted_1, [
          (appointmentsNotifications.value.length > 0)
            ? (_openBlock(), _createBlock(NotificationsContainer, {
                key: 0,
                title: "Upcoming Appointments",
                onClearAllClicked: _cache[0] || (_cache[0] = ($event: any) => (clearAppointments()))
              }, {
                default: _withCtx(() => [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(appointmentsNotifications.value, (notification, index) => {
                    return (_openBlock(), _createBlock(NotificationCard, {
                      key: index,
                      notification: notification,
                      index: index,
                      "background-color": "#FFFFE1",
                      onNotificationClicked: ($event: any) => (loadNotificationRecord(notification)),
                      onRemoveClicked: ($event: any) => (removeAppointment(notification, index))
                    }, null, 8, ["notification", "index", "onNotificationClicked", "onRemoveClicked"]))
                  }), 128))
                ]),
                _: 1
              }))
            : _createCommentVNode("", true),
          (tasksNotifications.value.length > 0)
            ? (_openBlock(), _createBlock(NotificationsContainer, {
                key: 1,
                title: "New Tasks",
                onClearAllClicked: _cache[1] || (_cache[1] = ($event: any) => (clearTasks()))
              }, {
                default: _withCtx(() => [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(tasksNotifications.value, (notification, index) => {
                    return (_openBlock(), _createBlock(NotificationCard, {
                      key: index,
                      notification: notification,
                      index: index,
                      "background-color": "#E6EFF9",
                      onNotificationClicked: ($event: any) => (loadNotificationRecord(notification)),
                      onRemoveClicked: ($event: any) => (removeTask(notification, index))
                    }, null, 8, ["notification", "index", "onNotificationClicked", "onRemoveClicked"]))
                  }), 128))
                ]),
                _: 1
              }))
            : _createCommentVNode("", true),
          (systemNotifications.value.length > 0)
            ? (_openBlock(), _createBlock(NotificationsContainer, {
                key: 2,
                title: "System Notifications",
                onClearAllClicked: _cache[2] || (_cache[2] = ($event: any) => (clearSystemNotifications()))
              }, {
                default: _withCtx(() => [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(systemNotifications.value, (notification, index) => {
                    return (_openBlock(), _createBlock(NotificationCard, {
                      key: index,
                      notification: notification,
                      index: index,
                      "background-color": "#FFECD9",
                      onNotificationClicked: ($event: any) => (loadNotificationRecord(notification)),
                      onRemoveClicked: ($event: any) => (removeSystemNotification(notification, index))
                    }, null, 8, ["notification", "index", "onNotificationClicked", "onRemoveClicked"]))
                  }), 128))
                ]),
                _: 1
              }))
            : _createCommentVNode("", true),
          (returnedSignableNotifications.value.length > 0)
            ? (_openBlock(), _createBlock(NotificationsContainer, {
                key: 3,
                title: "Returned Signable Documents",
                onClearAllClicked: _cache[3] || (_cache[3] = ($event: any) => (clearReturnedSignableDocuments()))
              }, {
                default: _withCtx(() => [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(returnedSignableNotifications.value, (notification, index) => {
                    return (_openBlock(), _createBlock(NotificationCard, {
                      key: index,
                      notification: notification,
                      index: index,
                      "background-color": "#FFFFE1",
                      onNotificationClicked: ($event: any) => (loadNotificationRecord(notification)),
                      onRemoveClicked: ($event: any) => (
              removeReturnedSignableDocument(notification, index)
            )
                    }, null, 8, ["notification", "index", "onNotificationClicked", "onRemoveClicked"]))
                  }), 128))
                ]),
                _: 1
              }))
            : _createCommentVNode("", true),
          (maintenanceRequestNotifications.value.length > 0)
            ? (_openBlock(), _createBlock(NotificationsContainer, {
                key: 4,
                title: "Maintenance Requests",
                onClearAllClicked: _cache[4] || (_cache[4] = ($event: any) => (clearMaintenanceRequests()))
              }, {
                default: _withCtx(() => [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(maintenanceRequestNotifications.value, (notification, index) => {
                    return (_openBlock(), _createBlock(NotificationCard, {
                      key: index,
                      notification: notification,
                      index: index,
                      "background-color": "#FFFFE1",
                      onNotificationClicked: ($event: any) => (loadNotificationRecord(notification)),
                      onRemoveClicked: ($event: any) => (removeMaintenanceRequest(notification, index))
                    }, null, 8, ["notification", "index", "onNotificationClicked", "onRemoveClicked"]))
                  }), 128))
                ]),
                _: 1
              }))
            : _createCommentVNode("", true),
          (_unref(notificationsStore).notificationCount == 0)
            ? (_openBlock(), _createElementBlock("span", _hoisted_2, "There are no notifications to display."))
            : _createCommentVNode("", true)
        ])
      ]),
      _: 1
    }, 8, ["modelValue", "target", "height", "loading"]),
    _createVNode(EditTaskModal, {
      modelValue: displayEditTaskModal.value,
      "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((displayEditTaskModal).value = $event)),
      id: selectedNotificationId.value,
      taskIsNotification: true
    }, null, 8, ["modelValue", "id"]),
    _createVNode(EditAppointmentModal, {
      modelValue: displayEditAppointmentModal.value,
      "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((displayEditAppointmentModal).value = $event)),
      "appointment-id": selectedNotificationId.value,
      notificationAppointment: true
    }, null, 8, ["modelValue", "appointment-id"]),
    _createVNode(ReturnedSignableDocumentsModal, {
      modelValue: displayReturnedSignableDocumentsModal.value,
      "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((displayReturnedSignableDocumentsModal).value = $event)),
      reminderPageFilters: filters.value
    }, null, 8, ["modelValue", "reminderPageFilters"]),
    _createVNode(MaintenanceRequestsModal, {
      modelValue: displayMaintenanceRequestsModal.value,
      "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((displayMaintenanceRequestsModal).value = $event)),
      fromNotificationPopup: true
    }, null, 8, ["modelValue"])
  ], 64))
}
}

})